import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Grid, Typography, Divider, Paper, LinearProgress, Link } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { HiOutlineChevronDown } from '@react-icons/all-files/hi/HiOutlineChevronDown';
import { HiOutlineChevronUp } from '@react-icons/all-files/hi/HiOutlineChevronUp';
import { Redux, Slices } from 'spwa-ui-library';
import { useNavigate, useLocation } from '@reach/router';
import { CgNotes } from '@react-icons/all-files/cg/CgNotes';
import { TiLocationArrowOutline } from '@react-icons/all-files/ti/TiLocationArrowOutline';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import FeedbackForm from '../../components/FeedbackForm';
import OrderConfirm from './OrderConfirm';
import OrderSummary from '../../components/OrderSummary';

import DrawerPwa from '../../components/DrawerPwa';
import FeedbackPopup from '../Feedback/FeedbackPopup';
import StarRating from '../../components/StarRating';
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api';
import Header from '../../components/Header';
import LanguageButton from '../../components/LanguageButton';
import { Context } from '../../components/Multi-lang/Wrapper';
import { decimalChecker } from 'spwa-ui-library/esm/utils/helper';
import clock from './images/clock.svg';
import PizzaHutLogo from '../../brands/phd/logo';
import supportPhone from '../../components/OrderSummary/images/phonebtn.svg';
import DialogBox from '../../components/DialogBox';
import classNames from 'classnames';
import orderWait from './images/onway.svg';
import dineIn from './images/dineInReady.svg';
import driveThru from './images/onway.svg';
import carhopImg from './images/onway.svg';
import pickUpStore from './images/readypickup.svg';
import beingPrepare from './images/prepared.svg';
import destMarker from './images/destMarker.svg';
import css from './index.module.scss';
import Phpin from './images/PHpin.svg';
import map1 from './images/map1.svg';
import phoneIcon from './images/call.svg';
import parking from './images/parking.svg';
import delivery from './images/packagedelivery.svg';
import chat from './images/chat.svg';
import phn from './images/phone1.svg';
import storeLoc from './images/storeloc.svg';
import locationPin from './images/Shape.svg';

const { useSelector, useDispatch } = Redux;
const { selectDesktop } = Slices.appSlice;
const { getOrderDetail, orderStatus, sendFeedBack } = Slices.orderSlice;
const { selectPaymentMethodConstants, selectCountryValidations } = Slices.configSlice;
const { selectCurrentCountry } = Slices.profileSlice;
const { getCurrentProfile } = Slices.profileSlice;
const { selectStoreEntities } = Slices.storeSlice;

const MapCard = ({ detail, selectedStore, storeNameTitle, isDesktop }) => {
  const intl = useIntl();
  const description = (selectedStore && selectedStore[`address_${intl.locale}`]) || '';
  const hyphenIndex = description && description.indexOf('-', description.indexOf('-') + 1);
  const formattedDescription =
    hyphenIndex !== -1 ? description?.slice(0, hyphenIndex) + '...' : description;

  return (
    <div className={classNames(css.mapCard, { [css.mapCardMobile]: !isDesktop })}>
      <div className={css.section1}>
        {/* Image content for left section */}
        <img className="icon" src={storeLoc} alt="Store location" />
      </div>
      <div className={css.section2}>
        {/* Text content for center section */}
        <h2 className={css.heading}>
          <FormattedMessage id="pickup_order_from" defaultMessage="Pickup your order from" />
        </h2>
        <h2 className={css.subHeading}>{selectedStore && selectedStore[storeNameTitle]}</h2>
        <p className="description">{formattedDescription}</p>
      </div>
      <div className={`${css.section3} ${css.rightSection}`}>
        {/* Image content for right section */}
        {detail?.orderType !== `DELIVERY` && (
          <div className={css.getDirection}>
            <TiLocationArrowOutline size="18px" />

            <a
              href={`https://www.google.com/maps/dir//${detail?.address?.lat},${detail?.address?.lng}`}
              target="_blank"
              rel="noopener noreferrer"
            ></a>
          </div>
        )}
      </div>
    </div>
  );
};

const Cards = () => {
  const intl = useIntl();

  const data = [
    {
      step: 'Step 01',
      image: map1,
      text: intl.formatMessage({ id: 'curbside_1' }),
    },
    {
      step: 'Step 02',
      image: parking,
      text: intl.formatMessage({ id: 'curbside_2' }),
    },
    {
      step: 'Step 03',
      image: phoneIcon,
    },
    {
      step: 'Step 04',
      image: chat,
      text: intl.formatMessage({ id: 'curbside_5' }),
    },
    {
      step: 'Step 05',
      image: delivery,
      text: intl.formatMessage({ id: 'curbside_6' }),
    },
  ];

  return (
    <>
      <span className={css.curbsideInstStyle}>
        <FormattedMessage
          id="curbside_ins_order"
          defaultMessage="Here’s how you can get your order"
        />
      </span>
      <div className={css.cardsContainer}>
        {data.map((card, index) => (
          <div key={index} className={css.card}>
            <span className={css.step}>{card.step}</span>
            <img className={css.image} src={card.image} alt="Card Image" />
            <span className={css.text}>
              {card.step === 'Step 03' ? (
                <>
                  {`${intl.formatMessage({ id: 'curbside_3' })} `}
                  <img src={phn} alt="Phone Icon" />
                  {` ${intl.formatMessage({ id: 'curbside_4' })}`}
                </>
              ) : (
                card.text
              )}
            </span>
          </div>
        ))}
      </div>
    </>
  );
};

function TrackOrderDetail({ orderId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const [ordId, setOrderId] = useState(orderId);
  const [allStatus, setAllStatus] = useState('initial');
  const [infoStatus, setInfoStatus] = useState(null);
  const [detail, setDetail] = useState(null);
  const storeList = useSelector(selectStoreEntities);
  const [selectedStore, setSelectedStore] = React.useState({});
  const country = useSelector(selectCurrentCountry);
  const countryValidations = useSelector((state) => selectCountryValidations(state, country));
  const storeName = `name_${intl.locale}`;
  const addressString = `address_${intl.locale}`;

  // const [isFutureOrder, setisFutureOrder] = useState(false);
  // eslint:fix isFutureOrder is defined a value but is never used
  const [feedbackAccordState, setFeedbackAccordState] = useState(false);
  const config = useSelector((state) => state.config);
  const paymentMethodConst = useSelector(selectPaymentMethodConstants);
  const [ordStatus, setOrdStatus] = useState('PENDING');
  const [ratingSubmit, setRatingSubmit] = useState(false);
  const [feedbackSubmit, setFeedbackSubmit] = useState(false);
  const [progressBar, setProgressBar] = useState(true);
  const labels = config?.data?.data?.constants?.liveTrackingLabel || [];
  const setAccordionStatus = () => {
    setFeedbackAccordState(!feedbackAccordState);
  };
  const isDesktop = useSelector(selectDesktop);
  let oldDetail;
  let oldInfo;
  const getTrackOrderDetails = React.useCallback(() => {
    setProgressBar(true);

    dispatch(getOrderDetail({ orderId })).then((resp) => {
      if (resp.error) {
        navigate(`/${intl.locale}/home`);
      } else {
        oldDetail = resp?.payload?.data?.data;
        setDetail(resp?.payload?.data?.data);
      }
    });
    dispatch(orderStatus({ orderId })).then((resp) => {
      oldInfo = resp?.payload?.data?.data;
      setInfoStatus((prev) => resp?.payload?.data?.data);
      setOrdStatus((prev) => resp?.payload?.data?.data?.status);
      setAllStatus((prev) => 'again');
      setProgressBar(false);
    });
  });
  // console.log(detail, 'detail');

  const [stateOpen, setStateOpen] = useState(false);
  useEffect(() => {
    if (infoStatus && orderId !== infoStatus._id) {
      // window.location.reload();
      // setInfoStatus('initial');

      setDetail((prev) => null);
      setInfoStatus((prev) => null);
      getTrackOrderDetails();
      // setDetail((prev) => oldDetail);
      // setInfoStatus((prev) => oldInfo);

      // setAllStatus('initial');
    }
  }, [detail, getTrackOrderDetails, infoStatus, oldDetail, oldInfo, orderId]);
  const changeDrawerStatus = () => {
    setStateOpen(false);
    setSmileIndex(-1);
  };

  const context = useContext(Context);
  const currentUser = useSelector(getCurrentProfile);
  useEffect(() => {
    if (allStatus === 'initial') {
      setInfoStatus('initial');
      setDetail((prev) => null);
      setInfoStatus((prev) => null);
      setAllStatus('initial');
      // debugger;
      getTrackOrderDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStatus, orderId]);

  const submitFeedback = (feedbackObj) => {
    let payload = { ...feedbackObj };
    payload.rating = '';
    dispatch(sendFeedBack(payload));
    setStateOpen(true);
    setFeedbackSubmit(true);
  };
  const profile = useSelector((state) => state.profile);
  const countryName = profile?.country;
  const contrySpecificValidation = config?.data?.data?.contrySpecificValidation;
  const isExist = contrySpecificValidation?.findIndex(({ country }) => country === countryName);
  const path = 'tel: ' + contrySpecificValidation?.[isExist].customerCare;
  const currency = contrySpecificValidation?.[isExist]?.currency;

  const [smileIndex, setSmileIndex] = useState(-1);
  const [gradeIndex] = useState(-1);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process?.env?.REACT_APP_MAPS_API_KEY || '',
  });
  // GOOGLE MAP LOGIC HEREBY
  const [map, setMap] = useState(null);
  const containerStyle = {
    height: '23.9rem',
  };
  const storeLatitude = infoStatus?.store?.location?.latitude;
  const storeLongitude = infoStatus?.store?.location?.longitude;
  const addressLatitude = detail?.address?.lat;
  const addressLongitude = detail?.address?.lng;

  const storeMarker = {
    lat: storeLatitude !== undefined ? parseFloat(storeLatitude) : null,
    lng: storeLongitude !== undefined ? parseFloat(storeLongitude) : null,
  };

  const addressMarker = {
    lat: addressLatitude !== undefined ? parseFloat(addressLatitude) : null,
    lng: addressLongitude !== undefined ? parseFloat(addressLongitude) : null,
  };

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const polyLine = [
    {
      lat: storeMarker.lat,
      lng: storeMarker.lng,
    },
    {
      lat: addressMarker.lat,
      lng: addressMarker.lng,
    },
  ];

  const handleMapZoom = () => {
    if (!map || !storeMarker.lat || !addressMarker.lat) return;

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(new window.google.maps.LatLng(storeMarker.lat, storeMarker.lng));
    bounds.extend(new window.google.maps.LatLng(addressMarker.lat, addressMarker.lng));

    // Fit the map to the LatLngBounds
    map.fitBounds(bounds);
  };

  useEffect(() => {
    handleMapZoom();
  }, [map]);

  const submitStarRating = (index) => {
    if (index < 3) {
      navigate(`/${context.locale}/feedback`, {
        state: { fromPage: 'trackOrder', rating: index, orderId: infoStatus?._id },
      });
    } else {
      setSmileIndex(index);
      const payload = {
        rating: index,
        mdbOrderId: infoStatus?._id || '',
      };
      dispatch(sendFeedBack(payload));
      setStateOpen((pre) => true);
      setRatingSubmit((pre) => true);
    }
  };
  const onExitMemoized = React.useCallback((e) => navigate(-1), [navigate]);
  const handleOrderDetail = () => {
    navigate(`/${context.locale}/order-detail/` + orderId, {
      state: {
        oldLocation: location,
      },
    });
  };

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    if (search?.get('modal') === 'track') {
      getTrackOrderDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const filterMode = paymentMethodConst.filter((val) => val.name === detail?.payment?.name);
  const groupMode = paymentMethodConst.filter(
    (val) => val.name === detail?.payment?.paymentGroupCode
  );
  const sameMethod = detail?.payment?.name === detail?.payment?.paymentGroupCode;
  var d =
    detail?.advanceTime === 0
      ? new Date(detail?.createdAt)
      : new Date(detail?.advanceTime); /* midnight in China on April 13th */

  const formDate = `${d.getDate()} ${d.toLocaleDateString('en-US', {
    month: 'long',
  })}, ${d.getFullYear()}`;

  React.useEffect(() => {
    Object.values(storeList || {}).every((val) => {
      if (val.storeId === infoStatus?.store?.storeId) {
        setSelectedStore(val);
        return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoStatus]);
  // console.log(ordId, 'id');
  const storeNameTitle = `name_${intl.locale}`;
  React.useEffect(() => {
    if (detail?.country && profile.status === 'succeeded' && profile.country !== detail?.country) {
      navigate(`/${intl.locale}/home`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, profile?.country, profile?.status]);

  const scheduleMessage = () => {
    let message = null;
    switch (detail?.address?.addressSubType) {
      case 'DELIVERY':
        message = (
          <FormattedMessage id="delivery_order" defaultMessage="Delivery Order Scheduled" />
        );
        break;
      case 'CARHOP':
        message = <FormattedMessage id="carhop_order" defaultMessage="Carhop Order Scheduled" />;
        break;
      case 'DINEIN':
        message = <FormattedMessage id="dinein_order" defaultMessage="Dine-in Order Scheduled" />;
        break;
      case 'DRIVETHRU':
        message = (
          <FormattedMessage id="drivethru_order" defaultMessage="Drive-thru Order Scheduled" />
        );
        break;
      case 'STORE':
        message = <FormattedMessage id="pickup_order" defaultMessage="Pickup Order Scheduled" />;
        break;
      default:
        message = null;
        break;
    }
    return <Typography className={css.headerText}>{message}</Typography>;
  };
  const orderImage = () => {
    let image = null;
    switch (detail?.address?.addressSubType) {
      case 'DELIVERY':
        image = orderWait;
        break;
      case 'CARHOP':
        image = carhopImg;
        break;
      case 'DINEIN':
        image = dineIn;
        break;
      case 'DRIVETHRU':
        image = driveThru;
        break;
      case 'STORE':
        image = pickUpStore;
        break;
      default:
        image = null;
        break;
    }
    return <img src={image} />;
  };
  // DELIVERY CARD
  const orderDeliverCard = () => {
    return (
      ((detail && detail?.advanceTime !== 0) || ordStatus === 'DELIVERED') && (
        <>
          <div
            className={classNames(css.storeDetailsContainer, {
              [css.storeLocDelivery]: ordStatus === 'DELIVERED',
            })}
          >
            <div className={css.storeWrap}>
              <figure className={css.locationPin}>
                <img
                  src={infoStatus?.address?.addressSubType !== 'DELIVERY' ? storeLoc : locationPin}
                  alt="location"
                />
              </figure>
              <div className={css.colBox}>
                <span className={css.storeAddress}>
                  <FormattedMessage id="pizzahut_store" defaultMessage="PizzHut Store" />
                </span>
                <span className={css.storeName}>{selectedStore[storeName]}</span>
                <span className={css.addressDescription}>
                  {selectedStore?.[addressString] || ''}
                </span>
              </div>
            </div>
            <Grid className={css.iconContainer}>
              <Link href={`tel:${countryValidations && countryValidations?.customerCare}`}>
                <img src={supportPhone} alt="Support Contact No." />
              </Link>
            </Grid>
          </div>
        </>
      )
    );
  };

  const mapOptions = {
    panControl: false,
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    draggable: false,
    overviewMapControl: false,
    rotateControl: false,
    disableDefaultUI: true,
    scrollwheel: false,
    disableDoubleClickZoom: true,
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  return (
    <div className={css.root} key={infoStatus?._id}>
      <Header
        title={<FormattedMessage id="navigation_trackOrder" defaultMessage="Track Order" />}
        actionButtons={[
          <LanguageButton />,
          !isDesktop && (
            <Button
              variant="outlined"
              size="small"
              className={css.resetBtn}
              onClick={() => getTrackOrderDetails()}
            >
              <FormattedMessage id="refresh" defaultMessage="REFRESH" />
            </Button>
          ),
        ]}
      />
      <main className={css.main}>
        {isDesktop && (
          <div className={css.desktopHeader}>
            <h1>
              <FormattedMessage id="order_status" />
            </h1>
          </div>
        )}
        <Grid className={isDesktop && css.desktopContainer}>
          <Grid className={css.wrapContent}>
            <Grid className={css.storeArea}>
              {isDesktop && (
                <Grid className={css.trackHeader}>
                  <div className={css.back}>
                    <FaArrowLeft onClick={onExitMemoized} />
                    <FormattedMessage id="navigation_trackOrder" />
                  </div>
                  <Button
                    variant="outlined"
                    size="small"
                    className={css.resetBtn}
                    onClick={() => getTrackOrderDetails()}
                  >
                    <FormattedMessage id="refresh" defaultMessage="REFRESH" />
                  </Button>
                </Grid>
              )}
              <Grid className={css.map}>
                {isLoaded ? (
                  <>
                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={storeMarker}
                      options={mapOptions}
                      onLoad={onLoad}
                      zoom={10}
                    >
                      <Marker position={storeMarker} icon={Phpin} />
                      {JSON.stringify(addressMarker) !== JSON.stringify(storeMarker) && (
                        <Marker position={addressMarker} icon={destMarker} />
                      )}
                      {detail?.orderType === 'DELIVERY' && (
                        <Polyline
                          path={polyLine}
                          options={{
                            geodesic: true,
                            strokeColor: '#FF0000',
                            strokeOpacity: 0.5,
                            strokeWeight: 2,
                          }}
                        />
                      )}
                    </GoogleMap>
                    <div className={css.desktopOrderLine}></div>
                  </>
                ) : (
                  <div className={css.loading}>Loading Map...</div>
                )}
                <>
                  {(detail && detail.orderType === 'DELIVERY') ||
                  (detail && detail?.advanceTime !== 0) ? null : (
                    <MapCard
                      detail={detail}
                      selectedStore={selectedStore}
                      storeNameTitle={storeNameTitle}
                      isDesktop={isDesktop}
                    />
                  )}
                </>
              </Grid>
              <Grid>{detail && detail?.advanceTime !== 0 ? orderDeliverCard() : null}</Grid>
              <div className={css.cardWrapper}>
                <Paper className={css.noShadow}>
                  {progressBar && (
                    <LinearProgress style={{ color: '#e4002b', backgroundColor: '#ffffff' }} />
                  )}
                  <Grid className={css.orderIdContainer}>
                    <Grid
                      className={classNames(css.orderIdDetails, {
                        [css.futureOrdStyle]: detail?.advanceTime !== 0,
                      })}
                    >
                      <Grid style={{ width: '100%' }}>
                        <Grid style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Grid style={{ display: 'flex' }}>
                            <PizzaHutLogo />
                            <Grid className={css.orderNoContainer}>
                              <Grid className={css.orderNoText}>
                                {detail?.orderDisplayNumber ? (
                                  <h5
                                    className={`${css.orbNumber} ${
                                      !isDesktop ? css.transparentBg : ''
                                    }`}
                                  >
                                    <FormattedMessage
                                      id={'orderdetails_orderno'}
                                      defaultMessage="Order No"
                                    />{' '}
                                    <span className={css.redText}>
                                      {detail?.orderDisplayNumber}
                                    </span>
                                  </h5>
                                ) : null}
                              </Grid>
                              <Grid className={css.amountText}>
                                <Typography>
                                  {!sameMethod ? (
                                    <>
                                      {groupMode.length ? (
                                        <span>{groupMode.length ? groupMode[0]?.name : ''}</span>
                                      ) : (
                                        ''
                                      )}
                                      <span className={css.subMode}>
                                        {' '}
                                        {filterMode[0]?.value === 'Pay by Cash'
                                          ? 'COD'
                                          : filterMode[0]?.value}
                                      </span>
                                    </>
                                  ) : (
                                    <> {groupMode?.length ? groupMode[0]?.value : ''}</>
                                  )}{' '}
                                  -
                                  <span style={{ marginInlineStart: '0.3rem' }}>
                                    {infoStatus?.store
                                      ? decimalChecker(
                                          infoStatus?.amount[infoStatus?.amount.length - 1]?.amount
                                        )
                                      : ''}{' '}
                                    {currency}
                                  </span>
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid>
                            <Button className={css.detailsButton} onClick={handleOrderDetail}>
                              <FormattedMessage id="trackorder_details" defaultMessage="Details" />
                            </Button>
                          </Grid>
                        </Grid>
                        {!isDesktop ? (
                          <Grid
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              gap: '2rem',
                              alignItems: 'center',
                              backgroundColor: 'rgba(225, 237, 255, 1)',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '0.4rem',
                              marginBlockStart: '1rem',
                            }}
                          >
                            <Typography>
                              <FormattedMessage id="trackorder_orderno" defaultMessage="Order no" />
                              .{' '}
                              <span className={css.boldText}>
                                {detail?.store ? detail?.country : ''} -{' '}
                                {detail?.store ? detail?.uOrderRef : ''}
                              </span>
                            </Typography>
                            <span>{` | `}</span>
                            <Typography>
                              <span>{detail?.createdAt ? formDate : ''}</span>
                            </Typography>
                          </Grid>
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
                {/* CURBSIDE CASE */}
                {detail?.address?.addressSubType === 'CARHOP' &&
                detail &&
                detail?.advanceTime === 0 ? (
                  <Cards />
                ) : null}
                <Paper className={css.orderStatusContainer}>
                  <Grid>
                    {detail && detail?.advanceTime !== 0 && detail?.status === 'PENDING' ? (
                      //TODO: remove this and make it global
                      <React.Fragment>
                        {detail?.advanceTime !== 0 ? null : (
                          <Grid className={css.statusCenter}>
                            <Typography>
                              <FormattedMessage
                                id="trackorder_currentstatus"
                                defaultMessage="Current Status"
                              />
                            </Typography>
                            <Grid>
                              <Typography>
                                <FormattedMessage
                                  id="trackorder_orderno"
                                  defaultMessage="Order no"
                                />
                                .{' '}
                                <span>
                                  {detail?.store ? detail?.country : ''} -{' '}
                                  {detail?.store ? detail?.uOrderRef : ''}
                                </span>
                              </Typography>
                              <span>{` | `}</span>
                              <Typography>
                                <span>{detail?.createdAt ? formDate : ''}</span>
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                        <Grid className={css.futureOrderContainer}>
                          <Grid>{orderImage()}</Grid>
                          <div className={css.futureWrap}>
                            <Grid>{scheduleMessage()}</Grid>
                            <Grid className={css.descContainer}>
                              <img src={clock} alt="Clock" />
                              <Typography className={css.descText}>{formDate}</Typography>
                            </Grid>
                          </div>
                        </Grid>
                        <Grid className={css.futureDivider}>
                          <Divider className={css.hrDivider} />
                        </Grid>
                        <Grid className={css.futureOrderWarn}>
                          <img src={beingPrepare} alt="" />
                          <Typography className={css.descText}>
                            <FormattedMessage
                              id="future_order_warn"
                              defaultMessage="We will start preparing your order 90 minutes prior to the scheduled time."
                            />
                          </Typography>
                        </Grid>
                      </React.Fragment>
                    ) : (
                      <>
                        {ordStatus === 'DELIVERED' ? null : (
                          <Grid className={css.status}>
                            <Typography className={css.redText}>
                              <FormattedMessage
                                id="trackorder_currentstatus"
                                defaultMessage="Current Status"
                              />
                            </Typography>
                            {isDesktop ? (
                              <Grid
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'flex-end',
                                  gap: '2rem',
                                  alignItems: 'center',
                                  backgroundColor: 'rgba(225, 237, 255, 1)',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '0.4rem',
                                }}
                              >
                                <Typography>
                                  <FormattedMessage
                                    id="trackorder_orderno"
                                    defaultMessage="Order no"
                                  />
                                  .{' '}
                                  <span className={css.boldText}>
                                    {detail?.store ? detail?.country : ''} -{' '}
                                    {detail?.store ? detail?.uOrderRef : ''}
                                  </span>
                                </Typography>
                                <span>{` | `}</span>
                                <Typography>
                                  <span>{detail?.createdAt ? formDate : ''}</span>
                                </Typography>
                              </Grid>
                            ) : null}
                          </Grid>
                        )}
                        <OrderConfirm
                          orderInfo={infoStatus || []}
                          orderDetail={detail}
                          status={ordStatus}
                          labels={labels}
                        />
                      </>
                    )}
                  </Grid>
                </Paper>
              </div>
            </Grid>
            <Grid className={css.orderStatusGrid}>
              <Grid>{ordStatus === 'DELIVERED' ? orderDeliverCard() : null}</Grid>
              <Paper className={css.feedbackArea}>
                {!feedbackSubmit ? (
                  <>
                    {detail && detail?.advanceTime !== 0 && detail?.status === 'PENDING' ? null : (
                      <Grid>
                        <Grid
                          className={
                            ordStatus === 'PENDING' ? css.disableGrid : css.feedbackContainer
                          }
                        >
                          <Grid className={css.feedbackClick} onClick={setAccordionStatus}>
                            <Grid className={css.iconText}>
                              <CgNotes className={css.icon} />
                              <Typography className={css.text}>
                                <FormattedMessage
                                  id="trackorder_feedback"
                                  defaultMessage="Feedback"
                                />
                              </Typography>
                            </Grid>
                            <Grid>
                              {feedbackAccordState ? (
                                <HiOutlineChevronUp className={css.arrow} />
                              ) : (
                                <HiOutlineChevronDown className={css.arrow} />
                              )}
                            </Grid>
                          </Grid>
                          <Grid>
                            <Typography className={css.feedbackDesc}>
                              <FormattedMessage
                                id="trackorder_sharefeedback"
                                defaultMessage="Share your feedback here for quick resolution"
                              />
                            </Typography>
                          </Grid>
                          {feedbackAccordState && (
                            <React.Fragment>
                              <Divider className={css.formDivider} />
                              <FeedbackForm
                                submitFeedback={submitFeedback}
                                smileIndex={gradeIndex}
                                orderId={infoStatus?._id}
                              />
                            </React.Fragment>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid>
                    <Grid className={css.feedbackContainer}>
                      <Grid className={css.feedbackClick}>
                        <Grid className={css.iconText}>
                          <CgNotes className={css.icon} />
                          <Typography className={css.text}>
                            <FormattedMessage
                              id="trackorder_feedbackthanks"
                              defaultMessage="Thanks for the feedback"
                            />
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid>
                        <Typography className={css.feedbackDesc}>
                          <FormattedMessage
                            id="trackorder_backtoemail"
                            defaultMessage="We will get back to you shortly at "
                          />
                          {currentUser?.email}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Paper>
              {!ratingSubmit ? (
                <Paper className={css.feedbackArea}>
                  <Grid className={ordStatus === 'PENDING' && css.disableGrid}>
                    <StarRating
                      submitStarRating={submitStarRating}
                      smileIndex={smileIndex}
                      isDesktop={isDesktop}
                      isFutureOrder={detail && detail?.advanceTime !== 0}
                    />
                  </Grid>
                </Paper>
              ) : null}
            </Grid>
          </Grid>

          {isDesktop && detail && (
            <Grid className={css.desktopGrid2}>
              <Paper>
                <OrderSummary orderInfo={detail} isDesktop={isDesktop} />
              </Paper>
            </Grid>
          )}
        </Grid>
      </main>
      {!isDesktop && (
        <DrawerPwa
          variant="temporary"
          anchor="bottom"
          open={stateOpen}
          onClose={changeDrawerStatus}
        >
          <FeedbackPopup
            onDrawerHandling={changeDrawerStatus}
            submitStarRating={submitStarRating}
            smileIndex={smileIndex}
          />
        </DrawerPwa>
      )}
      {isDesktop && (
        <DialogBox isOpen={stateOpen} onClose={changeDrawerStatus} hideCloseButton={true}>
          <FeedbackPopup
            onDrawerHandling={changeDrawerStatus}
            submitStarRating={submitStarRating}
            smileIndex={smileIndex}
          />
        </DialogBox>
      )}
      <footer className={css.footer}></footer>
    </div>
  );
}

export default TrackOrderDetail;
